import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './ConfirmationModal.module.css';

/**
 * Reusable confirmation modal for delete operations.
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Called when modal is closed/cancelled
 * @param {function} onConfirm - Called when user confirms action
 * @param {string} title - Modal title (e.g., "Delete Brand")
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Text for confirm button (default: "Delete")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - "danger" (red), "warning" (amber) or "primary"
 *        (blue, matching the Send Communications "Preview" button) (default: "danger")
 * @param {boolean} confirmDisabled - When true, the confirm button is disabled (default: false)
 * @param {React.ReactNode} children - Optional extra content rendered in the body
 *        beneath the message (e.g. a dropdown). Rendered outside the message
 *        paragraph so interactive controls produce valid markup.
 *
 * Accessibility (WCAG 2.1 AA):
 *  - Focus is moved into the modal on open (first focusable element).
 *  - Tab / Shift+Tab cycle within the modal (focus trap).
 *  - Escape closes the modal.
 *  - Focus is restored to the triggering element when the modal closes.
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
  variant = "danger",
  confirmDisabled = false,
  children = null
}) => {
  const modalRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Capture the element that had focus before the modal opened so we can
    // restore it when the modal closes.
    openerRef.current = document.activeElement;

    // Move focus into the modal (first focusable element).
    const modalNode = modalRef.current;
    if (modalNode) {
      const focusables = modalNode.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length > 0) {
        focusables[0].focus();
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !modalNode) return;

      const focusables = Array.from(
        modalNode.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Restore focus to the element that opened the modal. Guard against
      // the opener being detached from the DOM in the interim.
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function' && document.body.contains(opener)) {
        opener.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm();
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-modal-title"
    >
      <div className={styles.modal} ref={modalRef}>
        <div className={styles.header}>
          <h2 id="confirmation-modal-title" className={styles.title}>
            {title}
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.message}>{message}</p>
          {children}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            className={`${styles.confirmButton} ${
              variant === 'primary'
                ? styles.confirmButtonPrimary
                : variant === 'warning'
                ? styles.confirmButtonWarning
                : styles.confirmButtonDanger
            }`}
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;
